import * as React from "react";
import { Box, Stack, Typography, IconButton, Button, MenuItem, Select, CircularProgress } from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import PlaylistAddRoundedIcon from "@mui/icons-material/PlaylistAddRounded";
import {
  deletePayloadComposition,
  fetchPayloadCompositions,
  insertPayloadComposition,
  updatePayloadComposition,
} from "../../../api/queries";
import { createPendingEdits, subscribeToRows, upsertById } from "../../../api/sheetSync";
import type { EditedBy, PayloadCategory, PayloadCompositionRow, TradeOffScores } from "../../../types/payload";
import { AuthorTip } from "../sheetAuthors";

const MONO = "'Roboto Mono', 'Courier New', monospace";
const GROUP_BORDER = "rgba(168,85,247,0.45)";
const CELL_BORDER = "rgba(255,255,255,0.08)";

type Row = { id: string; optionId: string; scores: TradeOffScores; editedBy: EditedBy };
type CriterionKey = keyof TradeOffScores;

function fromDbRow(db: PayloadCompositionRow): Row {
  return {
    id: db.id,
    optionId: db.payload_option_id ?? "",
    editedBy: db.edited_by ?? {},
    // Number(): rows arriving over the live subscription aren't guaranteed
    // to carry numeric columns as JS numbers the way the REST API does.
    scores: {
      cost: { point: Number(db.cost_point), weight: Number(db.cost_weight) },
      energy: { point: Number(db.energy_point), weight: Number(db.energy_weight) },
      mass: { point: Number(db.mass_point), weight: Number(db.mass_weight) },
      custom: { point: Number(db.custom_point), weight: Number(db.custom_weight) },
      trl: { point: Number(db.trl_point), weight: Number(db.trl_weight) },
    },
  };
}

// Maps a criterion+field to its column in payload_compositions, e.g.
// ("cost", "point") -> "cost_point".
const dbColumn = (key: CriterionKey, field: "point" | "weight") => `${key}_${field}` as const;

function rowSum(scores: TradeOffScores): number {
  return (Object.keys(scores) as CriterionKey[]).reduce((sum, key) => sum + scores[key].point * scores[key].weight, 0);
}

// Integers only, typed by hand: a plain text input (no type="number" spinner
// arrows) that simply refuses any keystroke that wouldn't leave the field as
// a valid integer, rather than accepting and then reformatting it.
const INTEGER_RE = /^-?\d*$/;

function NumberCell(props: { value: number; onChange: (value: number) => void }) {
  const [text, setText] = React.useState(props.value ? String(props.value) : "");

  React.useEffect(() => {
    setText(props.value ? String(props.value) : "");
  }, [props.value]);

  return (
    <input
      type="text"
      inputMode="numeric"
      placeholder="0"
      value={text}
      onChange={(e) => {
        const next = e.target.value;
        if (!INTEGER_RE.test(next)) return; // reject the keystroke entirely

        setText(next);
        props.onChange(next === "" || next === "-" ? 0 : parseInt(next, 10));
      }}
      style={{
        width: "100%",
        minWidth: 56,
        background: "rgba(255,255,255,0.05)",
        border: "1px solid rgba(255,255,255,0.14)",
        borderRadius: 8,
        color: "#FFFFFF",
        fontFamily: MONO,
        fontSize: 13,
        fontWeight: 700,
        textAlign: "center",
        padding: "7px 4px",
        outline: "none",
      }}
      onFocus={(e) => (e.currentTarget.style.borderColor = "#A78BFA")}
      onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.14)")}
    />
  );
}

export default function TradeOffTable(props: { groupId: string; category: PayloadCategory }) {
  const { groupId, category } = props;
  const [rows, setRows] = React.useState<Row[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [adding, setAdding] = React.useState(false);
  const saveTimers = React.useRef<Record<string, number>>({});
  const pending = React.useRef(createPendingEdits());

  // Loads the group's rows for this category, then keeps them in step with
  // the other members' edits. Cells this browser is still editing keep
  // their local value (see createPendingEdits).
  React.useEffect(() => {
    let mounted = true;
    setLoading(true);

    const toRow = (db: PayloadCompositionRow) => fromDbRow(pending.current.merge(db.id, db));

    const load = () =>
      fetchPayloadCompositions(groupId, category.id).then(({ data, error }) => {
        if (!mounted) return;
        setLoading(false);
        if (error) return;
        setRows((data ?? []).map(toRow));
      });

    const unsubscribe = subscribeToRows<PayloadCompositionRow>(
      "payload_compositions",
      `group_id=eq.${groupId}`,
      (change) => {
        if (!mounted) return;
        if (change.type === "DELETE") {
          setRows((prev) => prev.filter((row) => row.id !== change.id));
          return;
        }
        if (change.row.category_id !== category.id) return;
        const row = toRow(change.row);
        setRows((prev) => upsertById(prev, row));
      },
      load
    );
    load();

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [groupId, category.id]);

  const setRow = (rowId: string, updater: (row: Row) => Row) => {
    setRows((prev) => prev.map((row) => (row.id === rowId ? updater(row) : row)));
  };

  const changeOption = (rowId: string, optionId: string) => {
    setRow(rowId, (row) => ({ ...row, optionId }));
    pending.current.mark(rowId, { payload_option_id: optionId || null });
    // Supabase's query builder is lazy -- the request only fires once
    // something consumes it via .then()/await, so every write below is
    // chained rather than called bare.
    updatePayloadComposition(rowId, { payload_option_id: optionId || null }).then(({ error }) => {
      if (error) console.error("Failed to save payload selection:", error.message);
    });
  };

  const changeCell = (rowId: string, key: CriterionKey, field: "point" | "weight", value: number) => {
    setRow(rowId, (row) => ({ ...row, scores: { ...row.scores, [key]: { ...row.scores[key], [field]: value } } }));
    pending.current.mark(rowId, { [dbColumn(key, field)]: value });

    // Debounced per-cell so a fast typist doesn't fire a request per
    // keystroke -- only the settled value after a short pause is saved.
    const timerKey = `${rowId}-${key}-${field}`;
    window.clearTimeout(saveTimers.current[timerKey]);
    saveTimers.current[timerKey] = window.setTimeout(() => {
      updatePayloadComposition(rowId, { [dbColumn(key, field)]: value }).then(({ error }) => {
        if (error) console.error("Failed to save trade-off cell:", error.message);
      });
    }, 500);
  };

  const addRow = async () => {
    setAdding(true);
    const { data, error } = await insertPayloadComposition(groupId, category.id, rows.length);
    setAdding(false);

    if (error || !data) return;
    setRows((prev) => upsertById(prev, fromDbRow(data)));
  };

  const removeRow = (rowId: string) => {
    setRows((prev) => prev.filter((row) => row.id !== rowId));
    pending.current.forget(rowId);
    Object.keys(saveTimers.current)
      .filter((k) => k.startsWith(`${rowId}-`))
      .forEach((k) => window.clearTimeout(saveTimers.current[k]));
    deletePayloadComposition(rowId).then(({ error }) => {
      if (error) console.error("Failed to delete row:", error.message);
    });
  };

  const groups: { key: CriterionKey; label: string }[] = [
    { key: "cost", label: "Cost [€]" },
    { key: "energy", label: "Energy [W]" },
    { key: "mass", label: "Mass [kg]" },
    { key: "custom", label: category.customCriterionLabel },
    { key: "trl", label: "TRL" },
  ];

  const headGroupSx = {
    px: 1.25,
    py: 1,
    borderLeft: `2px solid ${GROUP_BORDER}`,
    borderBottom: `1px solid ${GROUP_BORDER}`,
    bgcolor: "rgba(124,58,237,0.14)",
    fontFamily: MONO,
    fontWeight: 800,
    fontSize: 11.5,
    letterSpacing: "0.05em",
    textTransform: "uppercase" as const,
    color: "#E9D5FF",
    textAlign: "center" as const,
    whiteSpace: "nowrap" as const,
  };

  const headSubSx = (isGroupStart: boolean) => ({
    px: 1,
    py: 0.75,
    borderLeft: isGroupStart ? `2px solid ${GROUP_BORDER}` : `1px solid ${CELL_BORDER}`,
    borderBottom: `1px solid ${CELL_BORDER}`,
    bgcolor: "rgba(9,5,11,0.55)",
    fontFamily: MONO,
    fontWeight: 700,
    fontSize: 10,
    letterSpacing: "0.08em",
    textTransform: "uppercase" as const,
    color: "rgba(196,181,253,0.85)",
    textAlign: "center" as const,
    minWidth: 66,
  });

  const bodyCellSx = (isGroupStart: boolean, zebra: boolean) => ({
    px: 0.75,
    py: 0.85,
    borderLeft: isGroupStart ? `2px solid ${GROUP_BORDER}` : `1px solid ${CELL_BORDER}`,
    borderBottom: `1px solid ${CELL_BORDER}`,
    bgcolor: zebra ? "rgba(255,255,255,0.025)" : "transparent",
    textAlign: "center" as const,
  });

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap spacing={1}>
        <Typography sx={{ color: "#FFFFFF", fontWeight: 800, fontSize: { xs: 16, sm: 17.5 } }}>
          {category.label} trade-off
        </Typography>
        <Button
          size="small"
          onClick={addRow}
          disabled={adding || loading}
          startIcon={adding ? <CircularProgress size={14} sx={{ color: "inherit" }} /> : <AddRoundedIcon sx={{ fontSize: 16 }} />}
          sx={{
            textTransform: "none",
            fontWeight: 800,
            fontSize: 12.5,
            borderRadius: 1.5,
            color: "#FFFFFF",
            bgcolor: "#7C3AED",
            px: 1.5,
            "&:hover": { bgcolor: "#6D28D9" },
          }}
        >
          Add row
        </Button>
      </Stack>

      {loading ? (
        <Stack alignItems="center" sx={{ py: 5 }}>
          <CircularProgress size={22} sx={{ color: "#C4B5FD" }} />
        </Stack>
      ) : rows.length === 0 ? (
        <Stack
          alignItems="center"
          justifyContent="center"
          spacing={1.25}
          sx={{
            py: 5,
            borderRadius: 2,
            border: "1px dashed rgba(168,85,247,0.35)",
            bgcolor: "rgba(255,255,255,0.02)",
          }}
        >
          <PlaylistAddRoundedIcon sx={{ fontSize: 26, color: "rgba(196,181,253,0.6)" }} />
          <Typography sx={{ color: "rgba(226,232,240,0.6)", fontSize: 13.5, textAlign: "center", px: 2 }}>
            No rows yet. Add a row, pick a payload, and score it.
          </Typography>
          <Button
            size="small"
            onClick={addRow}
            disabled={adding}
            startIcon={adding ? <CircularProgress size={14} sx={{ color: "inherit" }} /> : <AddRoundedIcon sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              fontSize: 12.5,
              borderRadius: 1.5,
              color: "#E9D5FF",
              border: "1px solid rgba(168,85,247,0.4)",
              "&:hover": { bgcolor: "rgba(168,85,247,0.14)" },
            }}
          >
            Add row
          </Button>
        </Stack>
      ) : (
        <Box sx={{ overflowX: "auto", borderRadius: 2, border: `1px solid ${GROUP_BORDER}` }}>
          <Box component="table" sx={{ borderCollapse: "collapse", width: "100%", minWidth: 820 }}>
            <Box component="thead">
              <Box component="tr">
                <Box
                  component="th"
                  rowSpan={2}
                  sx={{ ...headGroupSx, minWidth: 180, textAlign: "left", borderLeft: "none" }}
                >
                  Payload
                </Box>
                {groups.map((g) => (
                  <Box component="th" key={g.key} colSpan={2} sx={headGroupSx}>
                    {g.label}
                  </Box>
                ))}
                <Box component="th" rowSpan={2} sx={{ ...headGroupSx, minWidth: 70 }}>
                  Sum
                </Box>
                <Box component="th" rowSpan={2} sx={{ ...headGroupSx, minWidth: 44 }} />
              </Box>
              <Box component="tr">
                {groups.map((g) => (
                  <React.Fragment key={g.key}>
                    <Box component="th" sx={headSubSx(true)}>
                      Point
                    </Box>
                    <Box component="th" sx={headSubSx(false)}>
                      Weight
                    </Box>
                  </React.Fragment>
                ))}
              </Box>
            </Box>

            <Box component="tbody">
              {rows.map((row, rowIndex) => (
                <Box component="tr" key={row.id}>
                  <Box component="td" sx={{ ...bodyCellSx(false, rowIndex % 2 === 1), borderLeft: "none", textAlign: "left" }}>
                    <AuthorTip editedBy={row.editedBy} field="payload_option_id">
                    <Select
                      size="small"
                      displayEmpty
                      value={row.optionId}
                      onChange={(e) => changeOption(row.id, e.target.value)}
                      sx={{
                        minWidth: 168,
                        fontSize: 12.5,
                        color: row.optionId ? "#FFFFFF" : "rgba(226,232,240,0.45)",
                        fontFamily: MONO,
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(168,85,247,0.3)" },
                        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(168,85,247,0.55)" },
                        "& .MuiSvgIcon-root": { color: "rgba(226,232,240,0.6)" },
                      }}
                      MenuProps={{ PaperProps: { sx: { bgcolor: "#1A1023", color: "#FFFFFF" } } }}
                    >
                      <MenuItem value="" disabled sx={{ fontSize: 12.5, fontStyle: "italic" }}>
                        Select payload…
                      </MenuItem>
                      {category.options.map((option) => (
                        <MenuItem key={option.id} value={option.id} sx={{ fontSize: 12.5 }}>
                          {option.manufacturer ? `${option.name} — ${option.manufacturer}` : option.name}
                        </MenuItem>
                      ))}
                    </Select>
                    </AuthorTip>
                  </Box>

                  {groups.map((g, gIndex) => (
                    <React.Fragment key={g.key}>
                      <Box component="td" sx={bodyCellSx(true, rowIndex % 2 === 1)}>
                        <AuthorTip editedBy={row.editedBy} field={dbColumn(g.key, "point")}>
                          <NumberCell
                            value={row.scores[g.key].point}
                            onChange={(point) => changeCell(row.id, g.key, "point", point)}
                          />
                        </AuthorTip>
                      </Box>
                      <Box component="td" sx={bodyCellSx(false, rowIndex % 2 === 1)}>
                        <AuthorTip editedBy={row.editedBy} field={dbColumn(g.key, "weight")}>
                          <NumberCell
                            value={row.scores[g.key].weight}
                            onChange={(weight) => changeCell(row.id, g.key, "weight", weight)}
                          />
                        </AuthorTip>
                      </Box>
                    </React.Fragment>
                  ))}

                  <Box
                    component="td"
                    sx={{
                      ...bodyCellSx(true, rowIndex % 2 === 1),
                      fontFamily: MONO,
                      fontWeight: 800,
                      fontSize: 14,
                      color: "#6EE7B7",
                    }}
                  >
                    {rowSum(row.scores)}
                  </Box>

                  <Box component="td" sx={bodyCellSx(true, rowIndex % 2 === 1)}>
                    <IconButton size="small" onClick={() => removeRow(row.id)} aria-label="Remove row">
                      <DeleteOutlineRoundedIcon sx={{ fontSize: 17, color: "rgba(248,113,113,0.75)" }} />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}

      <Typography sx={{ fontFamily: MONO, fontSize: 11.5, color: "rgba(226,232,240,0.5)" }}>
        Sum = Σ (point × weight) across all five criteria. Point and weight only accept whole numbers.
      </Typography>
    </Stack>
  );
}
